import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { MoonLoader } from "react-spinners";
import { manageAdminCustomersQueryOptions } from "../../../queryOptions/ManageUsersOptions";
import { getHearAboutUsStats } from "../../../apis/userApi";
import { AdminCustomer } from "../../../schemas/user";
import UserDetailsDialog from "./UserDetailsDialog";
import HearAboutUsPieChart from "../../../components/dashboard/HearAboutUsPieChart";

type UserPageTab = "users" | "hearAboutUs";
type UserStatusFilter = "all" | "members" | "enrollments";

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#f8f9fa",
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
  },
});

const ManageUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAdmin = localStorage.getItem("userType") === "admin";
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    manageAdminCustomersQueryOptions()
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userTab, setUserTab] = useState<UserPageTab>("users");
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>("all");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    email: true,
    address: true,
    isMember: true,
    isEnrolled: true,
    actions: true,
  });

  useEffect(() => {
    setColumnVisibilityModel({
      email: !isMobile,
      address: !isMobile,
      isMember: !isMobile,
      isEnrolled: !isMobile,
      actions: !isMobile,
    });
  }, [isMobile]);

  useEffect(() => {
    if (!isAdmin && userTab === "hearAboutUs") {
      setUserTab("users");
    }
  }, [isAdmin, userTab]);

  const customers = (data as AdminCustomer[]) || [];
  const membersCount = customers.filter((customer) => customer.isMember).length;
  const enrollmentsCount = customers.filter(
    (customer) => customer.isEnrolled
  ).length;

  const {
    data: hearAboutUsStats,
    isLoading: hearAboutUsLoading,
    isError: hearAboutUsError,
    refetch: refetchHearAboutUs,
    isFetching: hearAboutUsFetching,
  } = useQuery({
    queryKey: ["hear-about-us-stats"],
    queryFn: () => getHearAboutUsStats(),
    enabled: isAdmin && userTab === "hearAboutUs",
  });

  const rows = useMemo(() => {
    if (statusFilter === "members") {
      return customers.filter((customer) => customer.isMember);
    }
    if (statusFilter === "enrollments") {
      return customers.filter((customer) => customer.isEnrolled);
    }
    return customers;
  }, [customers, statusFilter]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    userId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewUser = (userId: string) => {
    setSelectedUserId(userId);
    setDialogOpen(true);
    handleMenuClose();
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row: AdminCustomer) =>
        `${row.firstName || ""} ${row.lastName || ""}`.trim(),
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "isMember",
      headerName: "Member",
      width: 160,
      renderCell: (params: GridRenderCellParams<AdminCustomer>) => (
        <Chip
          label={params.value ? "Active Member" : "Not a Member"}
          color={params.value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "isEnrolled",
      headerName: "Enrollment",
      width: 150,
      renderCell: (params: GridRenderCellParams<AdminCustomer>) => (
        <Chip
          label={params.value ? "Enrolled" : "No Enrollment"}
          color={params.value ? "info" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<AdminCustomer>) => (
        <>
          <Tooltip title="More Options">
            <IconButton
              size="small"
              aria-label={`Actions for ${params.row.firstName} ${params.row.lastName}`}
              onClick={(event) => {
                event.stopPropagation();
                handleMenuOpen(event, params.row.id);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedUserId === params.row.id}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleViewUser(params.row.id)}>
              <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
              View / Edit
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ px: { xs: 0, sm: 1, md: 2 }, py: { xs: 0.5, sm: 2, md: 3 } }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 2.5, md: 3 },
          borderRadius: { xs: 1.5, sm: 2 },
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            mb: { xs: 1.5, sm: 3 },
            gap: 1.5,
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="h1"
            fontWeight="bold"
          >
            Users
          </Typography>
          {isMobile ? (
            <IconButton
              color="primary"
              aria-label="Refresh"
              onClick={() => {
                refetch();
                if (userTab === "hearAboutUs") {
                  refetchHearAboutUs();
                }
              }}
              disabled={
                isFetching || (userTab === "hearAboutUs" && hearAboutUsFetching)
              }
            >
              {isFetching ||
              (userTab === "hearAboutUs" && hearAboutUsFetching) ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                isFetching ||
                (userTab === "hearAboutUs" && hearAboutUsFetching) ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={() => {
                refetch();
                if (userTab === "hearAboutUs") {
                  refetchHearAboutUs();
                }
              }}
              disabled={
                isFetching || (userTab === "hearAboutUs" && hearAboutUsFetching)
              }
            >
              Refresh
            </Button>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={userTab}
            onChange={(_event, value: UserPageTab) => setUserTab(value)}
            aria-label="user management tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab value="users" label="Users" />
            {isAdmin && (
              <Tab
                value="hearAboutUs"
                label="How did you hear about us"
              />
            )}
          </Tabs>
        </Box>

        {isAdmin && userTab === "hearAboutUs" && (
          hearAboutUsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 280,
                flexDirection: "column",
                gap: 2,
                px: 1,
              }}
            >
              <MoonLoader color="#0f766e" size={36} />
              <Typography color="text.secondary" textAlign="center">
                Loading signup feedback...
              </Typography>
            </Box>
          ) : hearAboutUsError ? (
            <Typography color="error">
              Failed to load signup feedback.
            </Typography>
          ) : (
            <HearAboutUsPieChart
              sources={hearAboutUsStats?.sources || []}
              otherDetails={hearAboutUsStats?.otherDetails || []}
              total={hearAboutUsStats?.total || 0}
            />
          )
        )}

        {userTab === "users" && (
          error ? (
            <Typography color="error">Failed to load users.</Typography>
          ) : (
            <Box>
              <Typography variant="body1" gutterBottom>
                Filter by status:
              </Typography>
              <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Button
                  variant={statusFilter === "members" ? "contained" : "outlined"}
                  onClick={() => setStatusFilter("members")}
                >
                  Members ({membersCount})
                </Button>
                <Button
                  variant={
                    statusFilter === "enrollments" ? "contained" : "outlined"
                  }
                  onClick={() => setStatusFilter("enrollments")}
                >
                  Enrollments ({enrollmentsCount})
                </Button>
                <Button
                  variant={statusFilter === "all" ? "contained" : "outlined"}
                  onClick={() => setStatusFilter("all")}
                >
                  All ({customers.length})
                </Button>
              </Box>
              <Box sx={{ height: 550, width: "100%" }}>
                <StyledDataGrid
                  key={statusFilter}
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  loading={isLoading}
                  density="standard"
                  disableRowSelectionOnClick
                  disableColumnFilter
                  disableDensitySelector
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={(model) =>
                    setColumnVisibilityModel(
                      model as typeof columnVisibilityModel
                    )
                  }
                  onRowClick={(params) => handleViewUser(params.row.id)}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                />
              </Box>
            </Box>
          )
        )}
      </Paper>

      <UserDetailsDialog
        open={dialogOpen}
        userId={dialogOpen ? selectedUserId : null}
        onClose={() => {
          setDialogOpen(false);
        }}
        onUpdated={() => {
          refetch();
        }}
      />
    </Box>
  );
};

export default ManageUsers;
